import { isNumber, uniqBy } from 'lodash';

import {
  type CompositionV3BreakdownOptions,
  type CompositionV3DataQuery,
  EntKind,
  type GetPayV2Query,
  PAY_V2_QUERY,
  type PayV2Entity,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../filters/types/filters';
import {
  type CompositionV3BreakdownDimension,
  getCompositionV3BreakdownOptions,
} from '../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import { getCompositionV3Filters } from '../../filters/utils/composition-v3/getCompositionV3Filters';
import {
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../filters/utils/composition-v3/getCompositionV3Variables';
import {
  getV2Filters,
  positionsEntityLookup,
} from '../../filters/utils/getV2Filters';
import { useChart } from '../Chart/ChartProvider';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import { PlotFilterType } from '../PageConfigProvider/types/item';
import { usePageState } from '../PageState/PageStateProvider';
import { CompositionRawDataProvider } from './composition/composition-v3/CompositionRawDataProvider';
import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
} from './composition/composition-v3/is-empty-composition-bucket';
import { TOP_COMPANIES_MAX, TOP_N_LIMIT } from './constants';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

type TopNDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  children: DataProviderChildren<TopEntity[] | undefined>;
};

export type SupportedSecondaryDimensions = Extract<
  SecondaryDimensionValues,
  'company' | 'geography' | 'role'
>;

export type TopEntity = {
  EntID: number;
  EntType: EntKind;
};

export const TopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  children,
}: TopNDataProviderProps) => {
  const { item } = useChart();
  const plotFilters = item?.plot?.plotFilters;

  const topNByHeadcountFilter = plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N_SAMPLE_SIZE
  )?.value;

  const topNFilterValue = plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N
  )?.value;

  const entityLimit =
    isNumber(topNFilterValue) &&
    Number.isFinite(topNFilterValue) &&
    topNFilterValue > 0
      ? topNFilterValue
      : TOP_N_LIMIT;

  const limit = isNumber(topNByHeadcountFilter)
    ? Math.min(entityLimit, topNByHeadcountFilter)
    : entityLimit;

  if (secondaryDimension === 'company') {
    return (
      <PayV2TopCompaniesProvider
        primaryDimension={primaryDimension}
        limit={Math.min(limit, TOP_COMPANIES_MAX)}
      >
        {children}
      </PayV2TopCompaniesProvider>
    );
  }

  return (
    <CompositionTopNProvider
      primaryDimension={primaryDimension}
      secondaryDimension={secondaryDimension}
      limit={limit}
    >
      {children}
    </CompositionTopNProvider>
  );
};

const PayV2TopCompaniesProvider = ({
  primaryDimension,
  limit,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  limit: number;
  children: DataProviderChildren<TopEntity[] | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const { geographyMode } = useFilterContext();
  const { item } = useChart();
  const { pageState } = usePageState();

  const selectedEntity = pageState.primaryEntities?.[item?.id];
  const selections = primaryFilter?.selections ?? [];

  const entities = payV2EntitiesFromSelections(
    selections.filter((selection) =>
      selectedEntity
        ? selection.levelId === selectedEntity.levelId &&
          selection.id.split('_').slice(-1)[0] === String(selectedEntity.id)
        : true
    )
  );

  const { customSkillTree } = useCustomSkillTaxonomyTree();
  const hasCustomSkillSelections = effectiveFilters.some(
    (f) =>
      f.id === FilterId.CUSTOM_SKILL &&
      f.type === FilterType.TREE &&
      f.selections.length > 0
  );
  const isWaitingForSkillTaxonomy =
    hasCustomSkillSelections && customSkillTree === null;

  const filters = getV2Filters({ primaryDimension, effectiveFilters });

  const [{ data, status }] = useUrqlQueryWithStatus({
    query: PAY_V2_QUERY,
    variables: {
      req: {
        entities,
        filters,
        ...(geographyMode === 'administrative' && {
          options: { geo_breakdown_state_over_msa: true },
        }),
      },
    },
    pause: isWaitingForSkillTaxonomy || entities.length === 0,
  });

  const topEntities = getTopCompaniesFromPayV2(data?.pay_v2)?.slice(0, limit);

  return children({ data: topEntities, status });
};

export const payV2EntitiesFromSelections = (
  selections: ReadonlyArray<{ levelId: string; id: string }>
): PayV2Entity[] =>
  selections
    .map((selection): PayV2Entity | null => {
      const EntType =
        positionsEntityLookup[
          selection.levelId as keyof typeof positionsEntityLookup
        ];
      const EntID = Number(selection.id.split('_').slice(-1)[0]);
      if (!EntType || !Number.isFinite(EntID)) return null;
      return { EntID, EntType };
    })
    .filter((entity): entity is PayV2Entity => entity !== null);

export const getTopCompaniesFromPayV2 = (
  payV2: GetPayV2Query['pay_v2']
): TopEntity[] | undefined => {
  if (!payV2) return undefined;
  const topCompanies = payV2.entities.flatMap(
    (entity) => entity.metrics.top_companies ?? []
  );
  return uniqBy(
    topCompanies.map(
      (company): TopEntity => ({
        EntID: company.index,
        EntType: EntKind.Company,
      })
    ),
    'EntID'
  );
};

const CompositionTopNProvider = ({
  primaryDimension,
  secondaryDimension,
  limit,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: Exclude<SupportedSecondaryDimensions, 'company'>;
  limit: number;
  children: DataProviderChildren<TopEntity[] | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { geographyMode } = useFilterContext();
  const { customSkillTree } = useCustomSkillTaxonomyTree();

  const normalizedEffectiveFilters =
    normalizeCompositionEffectiveFilters(effectiveFilters);
  const customSkillDeepestLevel = getCustomSkillDeepestLevelFromFilters(
    normalizedEffectiveFilters
  );
  const baseFilters = getCompositionV3Filters({
    primaryDimension,
    isSnapshot: true,
    effectiveFilters: normalizedEffectiveFilters,
  });
  const breakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: baseFilters,
    customSkillTree,
    customSkillDeepestLevel,
    geographyMode,
    includedBreakdownDimensions: [
      SECONDARY_DIMENSION_TO_BREAKDOWN[secondaryDimension],
    ],
  });
  const entType = getSegmentBreakdownEntKind(
    secondaryDimension,
    breakdownOptions
  );

  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      getSelectionBreakdowns
    >
      {({ data, status }) => {
        const composition = data?.composition_v3 as
          | NonNullable<CompositionV3DataQuery['composition_v3']>
          | null
          | undefined;
        const topEntities =
          composition && entType
            ? getTopEntitiesFromComposition({
                composition,
                segment: secondaryDimension,
                entType,
              })?.slice(0, limit)
            : undefined;
        return children({
          data: topEntities,
          status: entType === null ? DataProviderQueryStatus.Error : status,
        });
      }}
    </CompositionRawDataProvider>
  );
};

const SECONDARY_DIMENSION_TO_BREAKDOWN: Record<
  Exclude<SupportedSecondaryDimensions, 'company'>,
  CompositionV3BreakdownDimension
> = {
  geography: 'geography',
  role: 'role',
};

const getSegmentBreakdownEntKind = (
  secondaryDimension: Exclude<SupportedSecondaryDimensions, 'company'>,
  breakdownOptions: CompositionV3BreakdownOptions
): EntKind | null => {
  switch (secondaryDimension) {
    case 'geography':
      return breakdownOptions.geo_breakdown_level ?? null;
    case 'role':
      return breakdownOptions.role_breakdown_level ?? null;
  }
};

export const getTopEntitiesFromComposition = ({
  composition,
  segment,
  entType,
}: {
  composition: NonNullable<CompositionV3DataQuery['composition_v3']>;
  segment: Exclude<SupportedSecondaryDimensions, 'company'>;
  entType: EntKind;
}): TopEntity[] => {
  const breakdownRows = composition.entities.flatMap(
    (entity) => entity.metrics[segment] ?? []
  );

  const ranked = breakdownRows
    .filter(
      (row) =>
        !isEmptyCompositionBucket(row.id) && !isOtherCompositionBucket(row.id)
    )
    .sort((a, b) => a.rank - b.rank);

  return uniqBy(
    ranked.map((row): TopEntity => ({ EntID: row.id, EntType: entType })),
    'EntID'
  );
};
